<script setup lang="ts">
  import { computed } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { storeToRefs } from 'pinia';
  import { UiModalHeader, ColoredButton, ThemeProvider, IconImage } from '@/shared/ui';
  import { DocumentDropzone, useFileUploadStore } from '@/features/Document';
  import { useCustomToast, toastError } from '@/shared/lib';
  import LinkIcon from '@/shared/assets/icons/LinkIcon.svg';
  import useReplaceDocument from '../../model/useReplaceDocument';

  const props = defineProps<{
    title: string;
    documentId: number;
    revisionId: number;
    documentPrefix: string;
    isUrlDocument: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const {
    url,
    urlError,
    isSubmitting,
    canSave: canSaveFn,
    handleInput,
    handleUrlInput,
    handleConfirm,
  } = useReplaceDocument();
  const toast = useCustomToast();
  const fileUploadStore = useFileUploadStore();
  const { cleanState } = fileUploadStore;
  const { filesLoaded: filesLoadedRef } = storeToRefs(fileUploadStore);

  const canSave = computed(() => canSaveFn(props.isUrlDocument));

  const handleCloseInternalDocument = () => {
    emit('close');
  };

  const handleFilesSelectedInternalDocument = (files: File[]) => {
    if (files.length === 0) return;
    const file = files[0];
    if (!file.name.startsWith(props.documentPrefix)) {
      cleanState();
      toastError(
        toast,
        'Arquivo inválido',
        `O nome do arquivo deve começar com "${props.documentPrefix}"`
      );
    }
  };

  const handleConfirmInternalDocument = async () => {
    await handleConfirm(props.documentId, props.revisionId, props.isUrlDocument, handleCloseInternalDocument);
  };
</script>

<template>
  <VueFinalModal
    :click-to-close="false"
    :esc-to-close="false"
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-3/4 divide-y divide-gray-02"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
  >
    <ThemeProvider class="overflow-auto">
      <UiModalHeader
        :title="title"
        text-color="gray-07"
        class="px-6 py-4 text-sm border-b border-border-color"
        @close="handleCloseInternalDocument"
      />
      <div class="flex flex-col gap-5 max-h-[80vh] overflow-y-auto px-8 py-6">
        <template v-if="!isUrlDocument">
          <div class="flex flex-col gap-3 border border-gray-03 rounded-xl p-5 bg-gray-14">
            <p class="text-sm font-semibold text-gray-07">
              Requisitos para substituição de arquivo:
            </p>
            <ul class="list-disc pl-5 text-sm text-gray-07 space-y-1">
              <li>
                Arquivos podem ter as extensões:
                <span class="font-semibold">.pdf | .docx | .txt | .pptx | .xlsx | .xlsm</span>
              </li>
              <li>
                O formato obrigatório para o título do arquivo deve conter:
                <span class="font-semibold">[Prefixo]+[Numeração]+.[Sequencial].extensão</span>
              </li>
              <li>
                Exemplo:
                <span class="font-semibold">DA05.2.pdf</span>
              </li>
            </ul>
          </div>
          <DocumentDropzone
            :files-loaded="filesLoadedRef"
            :multiple="false"
            :is-external="false"
            @files-selected="handleFilesSelectedInternalDocument"
          />
        </template>

        <template v-else>
          <div class="border border-dashed border-gray-02 rounded-xl p-6">
            <div class="flex flex-col gap-2">
              <span class="text-sm font-semibold text-gray-07">Substituir URL do documento:</span>
              <div
                class="flex items-center gap-2 h-11 border rounded-lg px-4 bg-white-01 shadow-xs"
                :class="urlError ? 'border-red-500' : 'border-gray-02'"
              >
                <IconImage
                  :src="LinkIcon"
                  alt="URL"
                  class="text-gray-500"
                />
                <input
                  :value="url"
                  type="text"
                  class="flex-1 text-sm bg-transparent outline-none text-gray-07 placeholder:text-gray-04"
                  placeholder="Cole aqui a URL desejada"
                  @input="handleInput"
                  @blur="handleUrlInput"
                >
              </div>
              <p
                v-if="urlError"
                class="text-xs text-red-500"
              >
                {{ urlError }}
              </p>
            </div>
          </div>
        </template>

        <div class="flex justify-end gap-6 pr-4 pb-1.5">
          <ColoredButton
            text="Cancelar"
            :outlined="true"
            border-color="gray-03"
            color="gray-07"
            icon-alt="Cancelar"
            @click="handleCloseInternalDocument"
          />
          <ColoredButton
            :disabled="!canSave"
            disabled-color="light-gray-xx-02"
            :text="isSubmitting ? 'Salvando...' : 'Salvar'"
            icon-alt="Salvar"
            @click="handleConfirmInternalDocument"
          />
        </div>
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>
